import React, { FC, useState } from 'react';
import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { Button, ButtonColor } from 'components/uikit/buttons/Button.tsx';
import { QueryFactory } from 'services/api';
import styles from './CancelRevisionSigningConfirmationModal.module.scss';
import { Field } from 'components/uikit/Field.tsx';
import { Input } from 'components/uikit/inputs/Input.tsx';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { Trans } from 'react-i18next';
import { MultipleSelectorOption } from 'components/uikit/selector/MultipleSelector';
import { useSignatureBlockQueriesInvalidation } from 'pages/authorized/documents/documentList/signatureBlock/useSignatureBlockQueriesInvalidation';

export type CancelRevisionSigningConfirmationModalProps = {
  projectId: number;
  revisionId: number;
  options: MultipleSelectorOption[];
  isOpen: boolean;
  onClose: (closeParent: boolean) => void;
};

export const CancelRevisionSigningConfirmationModal: FC<
  CancelRevisionSigningConfirmationModalProps
> = (props) => {
  const i18n = useScopedTranslation(
    'Component.CancelRevisionSigningConfirmationModal',
  );
  const buttonTitle = useScopedTranslation('Button.Title');
  const [confirmDocumentName, setConfirmDocumentName] = useState('');
  const invalidateSignatureBlockQueries = useSignatureBlockQueriesInvalidation({
    revisionId: props.revisionId,
  });

  const isDeleteDisabled = !props.options
    .map((o) => o.label)
    .includes(confirmDocumentName);

  const onClose = (closeParent: boolean) => {
    setConfirmDocumentName('');
    props.onClose(closeParent);
  };

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={() => onClose(false)}
      isBlocking={true}
      title={i18n.t('title')}
    >
      <div className={styles.message}>
        <Trans
          i18nKey={'Component.CancelRevisionSigningConfirmationModal.message'}
          values={{
            names: props.options.map((o) => '«' + o.label + '»').join(', '),
          }}
          components={{
            red: <span className={styles.danger} />,
            p: <p />,
          }}
        />
        <Field className={styles.confirmField} title={i18n.t('confirm_field')}>
          <Input
            value={confirmDocumentName}
            onChange={(e) => {
              setConfirmDocumentName(e.target.value);
            }}
          ></Input>
        </Field>
      </div>
      <div className={styles.footer}>
        <Button
          title={buttonTitle.t('cancel')}
          color={ButtonColor.Secondary}
          className={styles.button}
          onClick={() => onClose(false)}
        />
        <Button
          testId={'delete-button'}
          title={buttonTitle.t('confirm')}
          color={ButtonColor.DestroyPrimary}
          className={styles.button}
          disabled={isDeleteDisabled}
          onClick={async () => {
            await QueryFactory.DocumentSigningClient.stopRevisionSigning(
              props.revisionId,
              props.options.map((option) => option.id as number),
            );
            await invalidateSignatureBlockQueries();
            onClose(true);
          }}
        />
      </div>
    </CustomModal>
  );
};
